import { useFeed } from "../hooks/useFeed";
import { PanelHeader } from "./PanelHeader";
import type { DistrictBoundary, DistrictPoi, DistrictStats } from "@kmitl/shared";

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";

interface BarRow {
  label: string;
  value: number;
  pct: number;       // 0..100 share of the max value in the row set
  swatch?: string;
}

function topN(items: Array<{ label: string; value: number }>, n: number, max?: number): BarRow[] {
  const head = items.slice(0, n);
  const m = max ?? Math.max(1, ...head.map((r) => r.value));
  return head.map((r) => ({ label: r.label, value: r.value, pct: (r.value / m) * 100 }));
}

function fmt(n: number): string {
  return n.toLocaleString("en-US");
}

/**
 * Lat Krabang district panel — three sections that pair with the
 * LATKRABANG lens:
 *   - District polygon from OSM (relation 3147500, admin_level=6)
 *   - OSM-tagged POIs (hospitals, schools, worship, etc.) inside the district
 *   - data.go.th CKAN aggregates (population, businesses, risks, religion, schools)
 *
 * The boundary + POIs go on the map as toggleable layers; the stats render
 * here as horizontal bar charts so a non-technical viewer can grok the
 * district shape in 10 seconds.
 */
export function LatKrabangPanel() {
  const boundary = useFeed<DistrictBoundary>(`${API_BASE}/api/district/lat-krabang/boundary`, 24 * 60 * 60_000);
  const pois = useFeed<DistrictPoi>(`${API_BASE}/api/district/lat-krabang/pois`, 24 * 60 * 60_000);
  const stats = useFeed<DistrictStats>(`${API_BASE}/api/district/lat-krabang/stats`, 6 * 60 * 60_000);

  const b = boundary.data[0];
  const ps = pois.data;
  const st = stats.data[0];

  const tier: import("@kmitl/shared").FallbackTier = boundary.fallbackTier === "loading" ? "scenario" : boundary.fallbackTier;
  const age = boundary.ageMinutes;
  const poiByCat: Record<string, number> = {};
  for (const p of ps) poiByCat[p.category] = (poiByCat[p.category] ?? 0) + 1;

  return (
    <div className="latkrabang-panel">
      <PanelHeader
        title="LAT KRABANG · DISTRICT"
        ageMinutes={age}
        fallbackTier={tier}
        source="osm-overpass · data.go.th"
      />

      {/* District shape summary */}
      <section className="latkrabang-section">
        <header className="latkrabang-h">DISTRICT SHAPE</header>
        {b ? (
          <div className="latkrabang-kv-grid">
            <div className="latkrabang-kv">
              <div className="num">{b.pointCount}</div>
              <div className="cap">polygon points</div>
            </div>
            <div className="latkrabang-kv">
              <div className="num">
                {b.bbox ? `${b.bbox.south.toFixed(3)}–${b.bbox.north.toFixed(3)}` : "—"}
              </div>
              <div className="cap">lat range</div>
            </div>
            <div className="latkrabang-kv">
              <div className="num">
                {b.bbox ? `${b.bbox.west.toFixed(3)}–${b.bbox.east.toFixed(3)}` : "—"}
              </div>
              <div className="cap">lng range</div>
            </div>
            <div className="latkrabang-kv">
              <div className="num">{b.relationId}</div>
              <div className="cap">OSM relation</div>
            </div>
          </div>
        ) : (
          <div className="caption mono">Loading district polygon…</div>
        )}
      </section>

      {/* POI breakdown */}
      <section className="latkrabang-section">
        <header className="latkrabang-h">DISTRICT POIs · {fmt(ps.length)}</header>
        {Object.keys(poiByCat).length === 0 ? (
          <div className="caption mono">No POIs returned from OSM Overpass.</div>
        ) : (
          <ul className="latkrabang-rows">
            {topN(
              Object.entries(poiByCat)
                .map(([k, v]) => ({ label: k, value: v }))
                .sort((a, b) => b.value - a.value),
              8,
            ).map((row) => (
              <li key={row.label} className="latkrabang-row">
                <span className="latkrabang-row-label mono">{row.label}</span>
                <span className="latkrabang-row-bar">
                  <span className="latkrabang-row-fill" style={{ width: `${row.pct}%` }} />
                </span>
                <span className="latkrabang-row-val mono">{fmt(row.value)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Population by subdistrict */}
      {st && (
        <section className="latkrabang-section">
          <header className="latkrabang-h">
            POPULATION · {fmt(st.population.total)} · M {fmt(st.population.byGender.male)} / F {fmt(st.population.byGender.female)}
          </header>
          <ul className="latkrabang-rows">
            {topN(
              st.population.bySubdistrict.map((s) => ({ label: s.subdistrict, value: s.total })),
              8,
            ).map((row) => (
              <li key={row.label} className="latkrabang-row">
                <span className="latkrabang-row-label mono">{row.label}</span>
                <span className="latkrabang-row-bar">
                  <span className="latkrabang-row-fill" style={{ width: `${row.pct}%` }} />
                </span>
                <span className="latkrabang-row-val mono">{fmt(row.value)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Businesses by type */}
      {st && st.businesses.total > 0 && (
        <section className="latkrabang-section">
          <header className="latkrabang-h">
            BUSINESSES · {fmt(st.businesses.total)} (top {Math.min(8, st.businesses.byType.length)} types)
          </header>
          <ul className="latkrabang-rows">
            {topN(st.businesses.byType.map((b) => ({ label: b.type, value: b.count })), 8).map((row) => (
              <li key={row.label} className="latkrabang-row">
                <span className="latkrabang-row-label mono">{row.label}</span>
                <span className="latkrabang-row-bar">
                  <span className="latkrabang-row-fill" style={{ width: `${row.pct}%` }} />
                </span>
                <span className="latkrabang-row-val mono">{fmt(row.value)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Risk points by category */}
      {st && st.risks.total > 0 && (
        <section className="latkrabang-section">
          <header className="latkrabang-h">
            RISK POINTS · {fmt(st.risks.total)}
          </header>
          <ul className="latkrabang-rows">
            {topN(st.risks.byType.map((r) => ({ label: r.type, value: r.count })), 8).map((row) => (
              <li key={row.label} className="latkrabang-row">
                <span className="latkrabang-row-label mono">{row.label}</span>
                <span className="latkrabang-row-bar">
                  <span className="latkrabang-row-fill" style={{ width: `${row.pct}%` }} />
                </span>
                <span className="latkrabang-row-val mono">{fmt(row.value)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Schools summary */}
      {st && st.schools.total > 0 && (
        <section className="latkrabang-section">
          <header className="latkrabang-h">
            SCHOOLS · {fmt(st.schools.total)} · {fmt(st.schools.totalStudents)} students · {fmt(st.schools.totalTeachers)} teachers
          </header>
          <ul className="latkrabang-rows">
            {topN(st.schools.byDistrict.map((s) => ({ label: s.district, value: s.students })), 8).map((row) => (
              <li key={row.label} className="latkrabang-row">
                <span className="latkrabang-row-label mono">{row.label}</span>
                <span className="latkrabang-row-bar">
                  <span className="latkrabang-row-fill" style={{ width: `${row.pct}%` }} />
                </span>
                <span className="latkrabang-row-val mono">{fmt(row.value)} students</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <footer className="caption mono latkrabang-foot">
        OSM relation {b?.relationId ?? 3147500} · เขตลาดกระบัง (BMA via data.go.th) · last sync {st?.fetchedAt?.slice(0, 16).replace("T", " ") ?? "—"}
      </footer>
    </div>
  );
}